import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../api";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import useEscapeKey from "../../hooks/useEscapeKey";

const PAY_META = {
  paid:    { label: "Paid",    bg: "#dcfce7", color: "#16a34a" },
  free:    { label: "Free",    bg: "#eff6ff", color: "#2563eb" },
  pending: { label: "Pending", bg: "#fff7ed", color: "#ea580c" },
  failed:  { label: "Failed",  bg: "#fef2f2", color: "#dc2626" },
};
const PayBadge = ({ status }) => {
  const m = PAY_META[status] || { label: status || "—", bg: "#f3f4f6", color: "#6b7280" };
  return (
    <span style={{ background: m.bg, color: m.color, fontWeight: 700, fontSize: 11, padding: "3px 9px", borderRadius: 10, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
      {m.label}
    </span>
  );
};

const STATUS_COLOR = {
  not_sent: "#9ca3af", sent_to_jury: "#f59e0b", scored: "#10b981",
  winner: "#6366f1", not_selected: "#ef4444", honorary: "#0891b2",
};
const STATUS_LABELS = {
  not_sent: "Not Sent", sent_to_jury: "Sent to Jury", scored: "Scored",
  winner: "Winner", not_selected: "Not Selected", honorary: "Honorary",
};

function getNomineeName(data) {
  if (!data) return "—";
  // Try combined firstName + lastName first
  const first = data.firstName || data.firstname || data.FirstName || "";
  const last  = data.lastName  || data.lastname  || data.LastName  || "";
  if (first || last) return [first, last].filter(Boolean).join(" ");

  const NAME_KEYS = ["fullname","fullName","name",
                     "nomineename","nomineeName","candidatename","candidateName","applicantname","applicantName"];
  const SKIP_TYPES = /^(http|https):\/\//;
  const SKIP_KEYS  = ["email","phone","mobile","jobtitle","jobTitle","organisation","organization",
                      "city","designation","salutation","speciality","leadsource","leadtype",
                      "crmeventid","leadownerid","nominationstatement","supportingdocument"];
  const entries = Object.entries(data);
  const byKey = entries.find(([k]) => NAME_KEYS.some(nk => k.toLowerCase().replace(/\s+/g,"") === nk));
  if (byKey?.[1]) return String(byKey[1]);
  const fallback = entries.find(([k,v]) =>
    v && typeof v === "string" && !SKIP_TYPES.test(v) &&
    !SKIP_KEYS.includes(k.toLowerCase().replace(/\s+/g,""))
  );
  return fallback ? String(fallback[1]) : "—";
}

function getNomineeEmail(data) {
  if (!data) return "";
  const entry = Object.entries(data).find(([k]) =>
    ["email","emailaddress","emailid"].includes(k.toLowerCase().replace(/\s+/g,""))
  );
  return entry ? String(entry[1]) : "";
}

const StatusBadge = ({ status }) => (
  <span style={{
    background: STATUS_COLOR[status] + "22", color: STATUS_COLOR[status],
    fontWeight: 700, fontSize: 11, padding: "3px 9px", borderRadius: 10,
    textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap",
  }}>
    {STATUS_LABELS[status] || status}
  </span>
);

// An average means nothing without its denominator — 9.0 from one juror is not
// 8.7 from seven. Amber until every juror who could score has scored.
const QuorumNote = ({ scored, expected }) => {
  const s = scored ?? 0;
  const e = expected ?? 0;
  if (e === 0) return null;
  const complete = s >= e;
  return (
    <span
      title={complete ? "Every juror on this panel has scored" : `${e - s} juror${e - s === 1 ? "" : "s"} still to score`}
      style={{
        display: "block", marginTop: 3, fontSize: 11, fontWeight: 700,
        color: complete ? "#059669" : s === 0 ? "#94A3B8" : "#B45309",
      }}
    >
      {s}/{e} jurors
    </span>
  );
};

const CategoryBadge = ({ value, sub }) => !value ? <span style={{ color: "#ccc" }}>—</span> : (
  <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
    <span title={value} style={{
      display: "inline-block", maxWidth: 180, background: "#f0f9ff", color: "#0369a1",
      border: "1px solid #bae6fd", padding: "3px 10px", borderRadius: 20, fontSize: 12,
      fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    }}>{value}</span>
    {sub && (
      <span title={sub} style={{
        display: "inline-block", maxWidth: 160, background: "#f5f3ff", color: "#6d28d9",
        border: "1px solid #ddd6fe", padding: "2px 9px", borderRadius: 20, fontSize: 11,
        fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>{sub}</span>
    )}
  </span>
);

// ── Import Nominations Modal ──────────────────────────────────────────────────
const IMPORT_FIELDS = [
  { key: "firstName",          label: "First Name",           required: true  },
  { key: "lastName",           label: "Last Name"                             },
  { key: "email",              label: "Email",                required: true  },
  { key: "mobile",             label: "Mobile"                                },
  { key: "jobTitle",           label: "Job Title"                             },
  { key: "organisation",       label: "Organisation"                          },
  { key: "city",               label: "City"                                  },
  { key: "nominationStatement", label: "Nomination Statement"                 },
  { key: "documentUrl",        label: "Document URL (link)"                   },
  { key: "nominationCategory", label: "Nomination Category"                   },
  { key: "nominationSubCategory", label: "Nomination Sub-category"            },
  { key: "paymentStatus",      label: "Payment Status"                        },
];

// Award categories can carry an optional second level. Forms saved before
// sub-categories existed only send the flat nominationCategories list.
const toCategoryGroups = (form) =>
  form?.nominationCategoryGroups?.length
    ? form.nominationCategoryGroups
    : (form?.nominationCategories || []).map(name => ({ _id: name, name, subCategories: [] }));

const subCategoriesOf = (groups, categoryName) =>
  groups.find(g => g.name === categoryName)?.subCategories || [];

// Label used for nominations submitted before / without a sub-category
const NO_SUB = "No sub-category";

function ImportNominationsModal({ eventId, nominationType, onClose, onSaved }) {
  const fileRef                 = useRef(null);
  const [step, setStep]         = useState(1); // 1=upload, 2=map, 3=preview
  const [forms, setForms]       = useState([]);
  const [formId, setFormId]     = useState("");
  const [rows, setRows]         = useState([]);   // parsed Excel rows
  const [headers, setHeaders]   = useState([]);   // Excel column headers
  const [mapping, setMapping]   = useState({});   // { importField: excelCol }
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    api.get(`/event-forms/event/${eventId}`)
      .then(res => {
        const nForms = (res.data.data || []).filter(f => f.nominationType === nominationType);
        setForms(nForms);
        if (nForms.length === 1) setFormId(nForms[0]._id);
      })
      .catch(() => {});
  }, [eventId, nominationType]);

  const handleFile = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb   = XLSX.read(ev.target.result, { type: "array" });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        if (data.length < 2) return toast.warning("File is empty or has no data rows");
        const hdrs = data[0].map(h => String(h).trim());
        setHeaders(hdrs);
        setRows(data.slice(1));
        // Auto-map columns whose name closely matches an import field
        const auto = {};
        IMPORT_FIELDS.forEach(({ key, label }) => {
          const idx = hdrs.findIndex(h =>
            h.toLowerCase().replace(/[\s_]/g, "") === key.toLowerCase() ||
            h.toLowerCase().replace(/[\s_]/g, "") === label.toLowerCase().replace(/\s/g, "")
          );
          if (idx !== -1) auto[key] = String(idx);
        });
        setMapping(auto);
        setStep(2);
      } catch { toast.error("Could not parse file. Please use .xlsx or .csv"); }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const preview = useMemo(() => rows.slice(0, 5).map(row => {
    const obj = {};
    IMPORT_FIELDS.forEach(({ key }) => {
      const col = mapping[key];
      obj[key] = col !== undefined && col !== "" ? String(row[Number(col)] ?? "") : "";
    });
    return obj;
  }), [rows, mapping]);

  const handleImport = async () => {
    if (!formId) return toast.warning("Select a nomination form first");
    const mappedKeys = Object.keys(mapping).filter(k => mapping[k] !== "");
    if (!mappedKeys.includes("firstName") || !mappedKeys.includes("email"))
      return toast.warning("At least First Name and Email columns must be mapped");

    setImporting(true);
    let ok = 0, fail = 0;
    for (let i = 0; i < rows.length; i++) {
      const row  = rows[i];
      const data = {};
      IMPORT_FIELDS.forEach(({ key }) => {
        const col = mapping[key];
        if (col !== undefined && col !== "") data[key] = String(row[Number(col)] ?? "").trim();
      });
      if (!data.firstName && !data.email) continue; // skip blank rows
      try {
        const { nominationCategory, nominationSubCategory, paymentStatus, documentUrl, ...submissionData } = data;
        if (documentUrl) submissionData.supportingDocument = documentUrl;
        const sub = await api.post("/form-submissions", {
          eventFormId:        formId,
          submissionData,
          nominationCategory: nominationCategory || undefined,
          nominationSubCategory: nominationSubCategory || undefined,
          paymentStatus:      paymentStatus      || "free",
        });
        if (paymentStatus && paymentStatus !== "free") {
          await api.patch(`/form-submissions/${sub.data.data._id}/status`, { paymentStatus });
        }
        ok++;
      } catch { fail++; }
      setProgress(Math.round(((i + 1) / rows.length) * 100));
    }
    setImporting(false);
    toast.success(`Imported ${ok} nominees${fail ? ` (${fail} failed)` : ""}`);
    onSaved(); onClose();
  };

  // ── Step 1: Upload ──────────────────────────────────────────────────────────
  const Step1 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Form selector */}
      <div>
        <label style={lbl}>Nomination Form <span style={{ color: "#ef4444" }}>*</span></label>
        {forms.length === 0
          ? <p style={{ fontSize: 13, color: "#9ca3af" }}>Loading forms…</p>
          : <select value={formId} onChange={e => setFormId(e.target.value)} style={inp}>
              <option value="">— Select form —</option>
              {forms.map(f => <option key={f._id} value={f._id}>{f.formName}</option>)}
            </select>
        }
      </div>

      {/* Drop zone */}
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { fileRef.current.files = e.dataTransfer.files; handleFile({ target: { files: [f] } }); } }}
        style={{ border: "2px dashed #bae6fd", borderRadius: 12, padding: "36px 24px", textAlign: "center", cursor: "pointer", background: "#f0f9ff", transition: "background 0.2s" }}
      >
        <div style={{ fontSize: 36, marginBottom: 8 }}>📂</div>
        <p style={{ fontWeight: 700, color: "#0369a1", margin: "0 0 4px", fontSize: 15 }}>Click or drag & drop your Excel / CSV file</p>
        <p style={{ color: "#9ca3af", fontSize: 12, margin: 0 }}>Supports .xlsx, .xls, .csv — first row must be headers</p>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={{ display: "none" }} />
      </div>
    </div>
  );

  // ── Step 2: Map fields ──────────────────────────────────────────────────────
  const Step2 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
        File has <strong>{rows.length}</strong> data rows and <strong>{headers.length}</strong> columns. Map each field below.
      </p>
      {IMPORT_FIELDS.map(({ key, label }) => (
        <div key={key} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", alignItems: "center", gap: 12 }}>
          <label style={{ ...lbl, margin: 0 }}>{label}{(key === "fullName" || key === "email") && <span style={{ color: "#ef4444" }}> *</span>}</label>
          <select value={mapping[key] ?? ""} onChange={e => setMapping(m => ({ ...m, [key]: e.target.value }))} style={inp}>
            <option value="">— Skip —</option>
            {headers.map((h, i) => <option key={i} value={String(i)}>{h}</option>)}
          </select>
        </div>
      ))}
    </div>
  );

  // ── Step 3: Preview ─────────────────────────────────────────────────────────
  const Step3 = () => (
    <div>
      <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 10 }}>Preview of first {Math.min(5, rows.length)} rows:</p>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#f8f9fa" }}>
              {IMPORT_FIELDS.filter(f => mapping[f.key] !== undefined && mapping[f.key] !== "").map(f => (
                <th key={f.key} style={{ padding: "6px 10px", fontWeight: 700, color: "#555", textAlign: "left", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" }}>{f.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.map((row, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                {IMPORT_FIELDS.filter(f => mapping[f.key] !== undefined && mapping[f.key] !== "").map(f => (
                  <td key={f.key} style={{ padding: "6px 10px", color: "#374151", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row[f.key] || <span style={{ color: "#d1d5db" }}>—</span>}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 13, color: "#6b7280", marginTop: 12, fontWeight: 600 }}>
        Total rows to import: <span style={{ color: "#111827" }}>{rows.length}</span>
      </p>
      {importing && (
        <div style={{ marginTop: 10 }}>
          <div style={{ background: "#f3f4f6", borderRadius: 6, height: 8, overflow: "hidden" }}>
            <div style={{ background: "#1D9AEC", width: `${progress}%`, height: "100%", transition: "width 0.3s" }} />
          </div>
          <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{progress}% — please wait…</p>
        </div>
      )}
    </div>
  );

  const steps = ["Upload File", "Map Fields", "Preview & Import"];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 580, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 12px 40px rgba(0,0,0,0.18)" }}>

        {/* Header */}
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #f3f4f6" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>Import Nominations from Excel</h3>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#9ca3af" }}>{nominationType} nominations</p>
            </div>
            <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: 15, color: "#6b7280", flexShrink: 0 }}>✕</button>
          </div>
          {/* Step indicator */}
          <div style={{ display: "flex", gap: 0, marginTop: 14 }}>
            {steps.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: step > i + 1 ? "#10b981" : step === i + 1 ? "#1D9AEC" : "#e5e7eb", color: step >= i + 1 ? "#fff" : "#9ca3af", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {step > i + 1 ? "✓" : i + 1}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: step === i + 1 ? "#1D9AEC" : step > i + 1 ? "#10b981" : "#9ca3af", whiteSpace: "nowrap" }}>{s}</span>
                </div>
                {i < steps.length - 1 && <div style={{ flex: 1, height: 1, background: step > i + 1 ? "#10b981" : "#e5e7eb", margin: "0 8px" }} />}
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1, padding: "18px 22px" }}>
          {step === 1 && <Step1 />}
          {step === 2 && <Step2 />}
          {step === 3 && <Step3 />}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 22px", borderTop: "1px solid #f3f4f6", display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={step === 1 ? onClose : () => setStep(s => s - 1)} style={btnSmall("#9ca3af")}>
            {step === 1 ? "Cancel" : "← Back"}
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            {step < 3 && (
              <button
                disabled={step === 1 && !formId}
                onClick={() => {
                  if (step === 1) { if (!formId) return toast.warning("Select a form first"); if (!rows.length) return toast.warning("Upload a file first"); }
                  setStep(s => s + 1);
                }}
                style={{ ...btnSmall("#1D9AEC"), opacity: (step === 1 && !formId) ? 0.5 : 1 }}>
                Next →
              </button>
            )}
            {step === 3 && (
              <button onClick={handleImport} disabled={importing} style={{ ...btnSmall("#10b981"), opacity: importing ? 0.6 : 1 }}>
                {importing ? `Importing… ${progress}%` : `Import ${rows.length} Nominees`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Add Nominee Modal ─────────────────────────────────────────────────────────
function AddNomineeModal({ eventId, nominationType, onClose, onSaved }) {
  const docFileRef = useRef(null);

  const [forms, setForms]               = useState([]);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [category, setCategory]         = useState("");
  const [subCategory, setSubCategory]   = useState("");
  const [paymentStatus, setPaymentStatus] = useState("free");
  const [fd, setFd] = useState({
    firstName: "", lastName: "", email: "", mobile: "",
    jobTitle: "", organisation: "", city: "", linkedIn: "", nominationStatement: "",
  });
  const [docFile,      setDocFile]      = useState(null);   // File object
  const [docUploading, setDocUploading] = useState(false);
  const [docUrl,       setDocUrl]       = useState("");     // uploaded S3 URL
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    api.get(`/event-forms/event/${eventId}`)
      .then(res => {
        const nForms = (res.data.data || []).filter(f => f.nominationType === nominationType);
        setForms(nForms);
        if (nForms.length === 1) setSelectedFormId(nForms[0]._id);
      })
      .catch(() => {});
  }, [eventId, nominationType]);

  const selectedForm = forms.find(f => f._id === selectedFormId);
  const categories    = toCategoryGroups(selectedForm);
  const subCategories = subCategoriesOf(categories, category);

  const handleDocPick = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDocFile(file);
    setDocUploading(true);
    try {
      const form = new FormData();
      form.append("documents", file);
      const res = await api.post("/form-submissions/upload-documents", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocUrl(res.data.data[0].url);
      toast.success("Document uploaded");
    } catch { toast.error("Document upload failed"); setDocFile(null); }
    setDocUploading(false);
  };

  const handleAdd = async () => {
    if (!selectedFormId)         return toast.warning("Select a nomination form first");
    if (!fd.firstName.trim())    return toast.warning("First name is required");
    if (!fd.email.trim())        return toast.warning("Email is required");
    if (subCategories.length > 0 && !subCategory) return toast.warning("Select a sub-category");
    setSaving(true);
    try {
      const submissionData = { ...fd };
      if (docUrl) submissionData.supportingDocument = docUrl;
      const sub = await api.post("/form-submissions", {
        eventFormId:        selectedFormId,
        submissionData,
        nominationCategory: category || undefined,
        nominationSubCategory: subCategory || undefined,
        paymentStatus,
      });
      if (paymentStatus !== "free") {
        await api.patch(`/form-submissions/${sub.data.data._id}/status`, { paymentStatus });
      }
      toast.success("Nominee added successfully");
      onSaved(); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add nominee");
    }
    setSaving(false);
  };

  const field = (key, label, opts = {}) => (
    <div key={key}>
      <label style={lbl}>{label}{opts.required && <span style={{ color: "#ef4444" }}> *</span>}</label>
      {opts.textarea
        ? <textarea value={fd[key]} onChange={e => setFd(p => ({ ...p, [key]: e.target.value }))}
            rows={4} placeholder={opts.placeholder || label}
            style={{ ...inp, resize: "vertical" }} />
        : <input type={opts.type || "text"} value={fd[key]}
            onChange={e => setFd(p => ({ ...p, [key]: e.target.value }))}
            style={inp} placeholder={opts.placeholder || label} />
      }
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 580, maxHeight: "93vh", display: "flex", flexDirection: "column", boxShadow: "0 12px 40px rgba(0,0,0,0.18)" }}>

        {/* Header */}
        <div style={{ padding: "18px 24px", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>Add Nominee Manually</h3>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#9ca3af", textTransform: "capitalize" }}>{nominationType} nomination</p>
          </div>
          <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: 15, color: "#6b7280" }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Form selector */}
          <div>
            <label style={lbl}>Nomination Form <span style={{ color: "#ef4444" }}>*</span></label>
            {forms.length === 0
              ? <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>Loading forms…</p>
              : <select value={selectedFormId} onChange={e => { setSelectedFormId(e.target.value); setCategory(""); setSubCategory(""); }} style={inp}>
                  <option value="">— Select form —</option>
                  {forms.map(f => <option key={f._id} value={f._id}>{f.formName}</option>)}
                </select>
            }
          </div>

          <div style={{ height: 1, background: "#f3f4f6" }} />

          {/* Nomination Category */}
          {categories.length > 0 && (
            <div>
              <label style={lbl}>Nomination Category</label>
              <select
                value={category}
                onChange={e => { setCategory(e.target.value); setSubCategory(""); }}
                style={inp}
              >
                <option value="">— Select category —</option>
                {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
              </select>

              {subCategories.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <label style={lbl}>Sub-category</label>
                  <select value={subCategory} onChange={e => setSubCategory(e.target.value)} style={inp}>
                    <option value="">— Select sub-category —</option>
                    {subCategories.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Nomination Statement */}
          {field("nominationStatement", "Nomination Statement", { textarea: true, placeholder: "Explain why this person deserves the award…" })}

          <div style={{ height: 1, background: "#f3f4f6" }} />

          {/* First + Last name */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {field("firstName", "First Name", { required: true })}
            {field("lastName",  "Last Name"  )}
          </div>

          {/* Email + Mobile */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {field("email",  "Email",  { required: true, type: "email" })}
            {field("mobile", "Mobile", { type: "tel" })}
          </div>

          {/* Job Title + Organisation */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {field("jobTitle",      "Job Title"    )}
            {field("organisation",  "Organisation" )}
          </div>

          {/* City + LinkedIn */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {field("city",     "City"         )}
            {field("linkedIn", "LinkedIn URL", { type: "url", placeholder: "https://linkedin.com/in/…" })}
          </div>

          {/* Supporting Document upload */}
          <div>
            <label style={lbl}>Supporting Document</label>
            <div
              onClick={() => docFileRef.current?.click()}
              style={{ border: "2px dashed #e5e7eb", borderRadius: 8, padding: "12px 16px", cursor: "pointer", background: docUrl ? "#f0fdf4" : "#f9fafb", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 22 }}>{docUrl ? "✅" : "📎"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {docUploading
                  ? <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>Uploading…</p>
                  : docUrl
                    ? <>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#16a34a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{docFile?.name}</p>
                        <a href={docUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 11, color: "#6b7280" }}>View uploaded file ↗</a>
                      </>
                    : <p style={{ margin: 0, fontSize: 13, color: "#9ca3af" }}>Click to upload PDF / image (optional)</p>
                }
              </div>
              {docUrl && <button type="button" onClick={e => { e.stopPropagation(); setDocUrl(""); setDocFile(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 16 }}>✕</button>}
            </div>
            <input ref={docFileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleDocPick} style={{ display: "none" }} />
          </div>

          <div style={{ height: 1, background: "#f3f4f6" }} />

          {/* Payment Status */}
          <div>
            <label style={lbl}>Payment Status</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {Object.entries(PAY_META).map(([k, v]) => (
                <button key={k} type="button" onClick={() => setPaymentStatus(k)}
                  style={{ padding: "7px 16px", borderRadius: 20, border: `2px solid ${paymentStatus === k ? v.color : "#e5e7eb"}`, background: paymentStatus === k ? v.bg : "#fff", color: paymentStatus === k ? v.color : "#6b7280", fontWeight: 700, fontSize: 12, cursor: "pointer", transition: "all 0.15s" }}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid #f3f4f6", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={btnSmall("#9ca3af")}>Cancel</button>
          <button onClick={handleAdd} disabled={saving || docUploading} style={{ ...btnSmall("#10b981"), opacity: (saving || docUploading) ? 0.6 : 1 }}>
            {saving ? "Adding…" : "Add Nominee"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Modal ────────────────────────────────────────────────────────────────
// Internal / CRM fields that should never be shown to admin for editing
const EDIT_SKIP_KEYS = new Set([
  "leadsource","leadtype","crmeventid","leadownerid","zohoStatus","zohoLeadId",
  "eventid","formid","submissionid","_id","__v","createdAt","updatedAt",
  "leadSource","leadType","crmEventId","leadOwnerId","eventId","formId",
]);
const EDIT_URL_KEYS = new Set([
  "supportingdocument","documenturl","fileurl","supportingDocument","documentUrl","fileUrl",
]);

function prettyLabel(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

function EditModal({ nomination, onClose, onSaved }) {
  const [categories,    setCategories]    = useState(toCategoryGroups(nomination.eventFormId));
  const [formData, setFormData]           = useState({ ...nomination.submissionData });
  const [category, setCategory]           = useState(nomination.nominationCategory || "");
  const [subCategory, setSubCategory]     = useState(nomination.nominationSubCategory || "");
  const [paymentStatus, setPaymentStatus] = useState(nomination.paymentStatus || "free");
  const [saving, setSaving]               = useState(false);

  const subCategories = subCategoriesOf(categories, category);

  // Fetch fresh form data so categories are never stale
  useEffect(() => {
    const formId = nomination.eventFormId?._id || nomination.eventFormId;
    if (!formId) return;
    api.get(`/event-forms/${formId}`)
      .then(res => setCategories(toCategoryGroups(res.data.data)))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/form-submissions/${nomination._id}/data`, {
        submissionData:     formData,
        nominationCategory: category || undefined,
        nominationSubCategory: subCategory || undefined,
      });
      await api.patch(`/form-submissions/${nomination._id}/status`, { paymentStatus });
      toast.success("Nomination updated");
      onSaved();
      onClose();
    } catch { toast.error("Update failed"); }
    setSaving(false);
  };

  // Split fields: url docs vs editable text/long-text
  const entries = Object.entries(formData).filter(([k]) => {
    const norm = k.toLowerCase().replace(/[\s_]/g, "");
    return !EDIT_SKIP_KEYS.has(norm) && !EDIT_SKIP_KEYS.has(k);
  });
  const docEntries  = entries.filter(([k, v]) => EDIT_URL_KEYS.has(k) || EDIT_URL_KEYS.has(k.toLowerCase().replace(/[\s_]/g,"")) || (typeof v === "string" && v.startsWith("http")));
  const textEntries = entries.filter(([k, v]) => !docEntries.some(([dk]) => dk === k));

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 600, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>

        {/* ── Header ── */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0F172A", letterSpacing: "-0.02em" }}>Edit Nomination</h3>
            <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#94A3B8" }}>{getNomineeName(nomination.submissionData)}</p>
          </div>
          <button onClick={onClose} style={{ background: "#F1F5F9", border: "none", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 16, color: "#64748B", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>

        {/* ── Body ── */}
        <div style={{ overflowY: "auto", flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Section: Nomination settings */}
          <div>
            <p style={sectionLabel}>Nomination Settings</p>
            <div style={{ display: "grid", gridTemplateColumns: categories.length > 0 ? "1fr 1fr" : "1fr", gap: 12 }}>

              {/* Category */}
              {categories.length > 0 && (
                <div>
                  <label style={lbl}>Nomination Category</label>
                  <select
                    value={category}
                    onChange={e => { setCategory(e.target.value); setSubCategory(""); }}
                    style={inp}
                  >
                    <option value="">— Select category —</option>
                    {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                  </select>

                  {subCategories.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <label style={lbl}>Sub-category</label>
                      <select value={subCategory} onChange={e => setSubCategory(e.target.value)} style={inp}>
                        <option value="">— Select sub-category —</option>
                        {subCategories.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Status */}
              <div>
                <label style={lbl}>Payment Status</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {Object.entries(PAY_META).map(([k, v]) => (
                    <button
                      key={k} type="button"
                      onClick={() => setPaymentStatus(k)}
                      style={{
                        padding: "7px 14px", borderRadius: 20,
                        border: `2px solid ${paymentStatus === k ? v.color : "#E2E8F0"}`,
                        background: paymentStatus === k ? v.bg : "#F8FAFC",
                        color: paymentStatus === k ? v.color : "#94A3B8",
                        fontWeight: 700, fontSize: 12, cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >{v.label}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div style={{ height: 1, background: "#F1F5F9" }} />

          {/* Section: Nominee details — 2-column grid */}
          {textEntries.length > 0 && (
            <div>
              <p style={sectionLabel}>Nominee Details</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px" }}>
                {textEntries.map(([key, val]) => {
                  const isLong = typeof val === "string" && val.length > 80;
                  return (
                    <div key={key} style={isLong ? { gridColumn: "1 / -1" } : {}}>
                      <label style={lbl}>{prettyLabel(key)}</label>
                      {isLong ? (
                        <textarea
                          value={String(val ?? "")}
                          onChange={e => setFormData(p => ({ ...p, [key]: e.target.value }))}
                          rows={3}
                          style={{ ...inp, resize: "vertical" }}
                        />
                      ) : (
                        <input
                          type="text"
                          value={String(val ?? "")}
                          onChange={e => setFormData(p => ({ ...p, [key]: e.target.value }))}
                          style={inp}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section: Documents (read-only links) */}
          {docEntries.length > 0 && (
            <>
              <div style={{ height: 1, background: "#F1F5F9" }} />
              <div>
                <p style={sectionLabel}>Documents</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {docEntries.map(([key, val]) => (
                    <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#F8FAFC", borderRadius: 8, border: "1px solid #E2E8F0" }}>
                      <span style={{ fontSize: 18 }}>📎</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em" }}>{prettyLabel(key)}</p>
                        <a href={String(val)} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#1D9AEC", fontWeight: 600, textDecoration: "none" }}>View document ↗</a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #F1F5F9", display: "flex", gap: 10, justifyContent: "flex-end", background: "#FAFAFA", borderRadius: "0 0 16px 16px" }}>
          <button onClick={onClose} style={btnSmall("#94A3B8")}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ ...btnSmall("#1D9AEC"), opacity: saving ? 0.6 : 1, boxShadow: saving ? "none" : "0 2px 8px rgba(29,154,236,0.3)" }}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

const sectionLabel = { margin: "0 0 10px", fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.07em" };

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AdminNominationsPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [nominations, setNominations] = useState(null);
  const [tab,         setTab]         = useState("individual");

  // Search / sort / filter
  const [search,   setSearch]   = useState("");
  const [sortBy,   setSortBy]   = useState("name");  // name | score | status
  const [sortDir,  setSortDir]  = useState("asc");
  const [statusFilter, setStatusFilter] = useState("all");

  const [payFilter,    setPayFilter]    = useState("all");
  const [addModal,     setAddModal]     = useState(false);
  const [importModal,  setImportModal]  = useState(false);
  const [viewMode,     setViewMode]     = useState("list");      // "list" | "categories"
  const [catSearch,    setCatSearch]    = useState("");
  const [catFilter,    setCatFilter]    = useState("");           // "" = all categories
  const [subFilter,    setSubFilter]    = useState("");           // "" = all sub-categories
  const [selectedIds,  setSelectedIds]  = useState([]);            // bulk selection
  const [bulkConfirm,  setBulkConfirm]  = useState(null);          // { status, label, danger }
  const [bulkRunning,  setBulkRunning]  = useState(false);
  const [bulkEmail,    setBulkEmail]    = useState(false);       // off by default — email is opt-in
  const [panelSize,    setPanelSize]    = useState(0);             // active jurors on this event

  // Modals
  const [remarksModal,  setRemarksModal]  = useState(null);
  const [remarksText,   setRemarksText]   = useState("");
  const [sendEmail,     setSendEmail]     = useState(true);
  const [editTarget,    setEditTarget]    = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }

  // Every screen-blocking modal closes on Escape
  useEscapeKey(!!remarksModal,  () => setRemarksModal(null));
  useEscapeKey(!!deleteConfirm, () => setDeleteConfirm(null));
  useEscapeKey(!!editTarget,    () => setEditTarget(null));
  useEscapeKey(!!bulkConfirm && !bulkRunning, () => setBulkConfirm(null));
  useEscapeKey(addModal,        () => setAddModal(false));
  useEscapeKey(importModal,     () => setImportModal(false));

  const fetchNominations = () => {
    api.get(`/jury/nominations/event/${eventId}`)
      .then(res => {
        setNominations(res.data.data || []);
        setPanelSize(res.data.panelSize || 0);
      })
      .catch(() => setNominations([]));
  };

  useEffect(() => { fetchNominations(); }, [eventId]);

  // ── Derived list ────────────────────────────────────────────────────────────
  const displayed = useMemo(() => {
    if (!nominations) return [];
    const q = search.trim().toLowerCase();
    let list = nominations.filter(n => n.eventFormId?.nominationType === tab);

    if (statusFilter !== "all") list = list.filter(n => n.juryStatus === statusFilter);
    if (payFilter   !== "all") list = list.filter(n => n.paymentStatus === payFilter);
    if (catFilter)             list = list.filter(n => (n.nominationCategory || "Uncategorized") === catFilter);
    if (subFilter)             list = list.filter(n => (n.nominationSubCategory || NO_SUB) === subFilter);

    if (q) list = list.filter(n => {
      const name = getNomineeName(n.submissionData).toLowerCase();
      const cat  = (n.nominationCategory || "").toLowerCase();
      const email = getNomineeEmail(n.submissionData).toLowerCase();
      return name.includes(q) || cat.includes(q) || email.includes(q);
    });

    list = [...list].sort((a, b) => {
      let va, vb;
      if (sortBy === "score") {
        va = a.averageScore ?? -1; vb = b.averageScore ?? -1;
        return sortDir === "asc" ? va - vb : vb - va;
      }
      if (sortBy === "status") {
        const order = ["not_sent","sent_to_jury","scored","winner","honorary","not_selected"];
        va = order.indexOf(a.juryStatus); vb = order.indexOf(b.juryStatus);
        return sortDir === "asc" ? va - vb : vb - va;
      }
      // name
      va = getNomineeName(a.submissionData).toLowerCase();
      vb = getNomineeName(b.submissionData).toLowerCase();
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });

    return list;
  }, [nominations, tab, search, sortBy, sortDir, statusFilter, payFilter, catFilter, subFilter]);

  // Sub-category breakdown for the category currently being viewed
  const subBreakdown = useMemo(() => {
    if (!nominations || !catFilter) return [];
    const map = {};
    nominations
      .filter(n => n.eventFormId?.nominationType === tab)
      .filter(n => (n.nominationCategory || "Uncategorized") === catFilter)
      .forEach(n => {
        const sub = n.nominationSubCategory || NO_SUB;
        map[sub] = (map[sub] || 0) + 1;
      });
    const entries = Object.entries(map).map(([name, count]) => ({ name, count }));
    // only worth showing when the category actually splits into sub-categories
    if (entries.length === 1 && entries[0].name === NO_SUB) return [];
    return entries.sort((a, b) => b.count - a.count);
  }, [nominations, tab, catFilter]);

  const tabCounts = useMemo(() => {
    if (!nominations) return {};
    return {
      individual:   nominations.filter(n => n.eventFormId?.nominationType === "individual").length,
      organisation: nominations.filter(n => n.eventFormId?.nominationType === "organisation").length,
    };
  }, [nominations]);

  // Category breakdown — computed separately for individual and organisation
  const CAT_PALETTE = ["#1D9AEC","#6366f1","#10b981","#f59e0b","#0891b2","#ec4899","#8b5cf6","#ef4444","#14b8a6","#f97316"];

  const buildCats = (type) => {
    if (!nominations) return [];
    const map = {};
    nominations.filter(n => n.eventFormId?.nominationType === type).forEach(n => {
      const cat = n.nominationCategory || "Uncategorized";
      if (!map[cat]) map[cat] = { name: cat, total: 0, not_sent: 0, sent: 0, scored: 0, decided: 0, scoreSum: 0, scoreCount: 0, subs: {} };
      map[cat].total++;
      if (n.nominationSubCategory) map[cat].subs[n.nominationSubCategory] = (map[cat].subs[n.nominationSubCategory] || 0) + 1;
      if (n.juryStatus === "not_sent")                                       map[cat].not_sent++;
      else if (n.juryStatus === "sent_to_jury")                              map[cat].sent++;
      else if (n.juryStatus === "scored")                                    map[cat].scored++;
      else if (["winner","honorary","not_selected"].includes(n.juryStatus))  map[cat].decided++;
      if (n.averageScore != null) { map[cat].scoreSum += n.averageScore; map[cat].scoreCount++; }
    });
    return Object.values(map)
      .map(c => ({
        ...c,
        avgScore: c.scoreCount > 0 ? Math.round((c.scoreSum / c.scoreCount) * 10) / 10 : null,
        subList: Object.entries(c.subs).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count),
      }))
      .sort((a, b) => b.total - a.total);
  };

  const catsByType = useMemo(() => ({
    individual:   buildCats("individual"),
    organisation: buildCats("organisation"),
  }), [nominations]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleTabSwitch = (t) => {
    setTab(t);
    setCatFilter("");
    setSubFilter("");
    setSelectedIds([]);
    // keep viewMode — if user is in category view, they want to see categories for the new tab too
    setSearch("");
    setStatusFilter("all");
    setPayFilter("all");
  };

  const deleteNomination = async (id) => {
    try {
      await api.delete(`/form-submissions/${id}`);
      toast.success("Nomination deleted");
      fetchNominations();
    } catch { toast.error("Failed to delete nomination"); }
    setDeleteConfirm(null);
  };

  const sendToJury = async (submissionId) => {
    try {
      await api.patch(`/jury/submission/${submissionId}/jury-status`, { juryStatus: "sent_to_jury" });
      toast.success("Sent to jury"); fetchNominations();
    } catch { toast.error("Failed"); }
  };

  // Steps a nomination back: a decision returns to scoring, scoring returns to draft
  const revertStatus = async (n) => {
    const target = ["winner", "honorary", "not_selected"].includes(n.juryStatus)
      ? (n.averageScore != null ? "scored" : "sent_to_jury")
      : "not_sent";
    const label = STATUS_LABELS[target] || target;
    if (!window.confirm(`Move this nomination back to "${label}"? No email is sent.`)) return;
    try {
      await api.patch(`/jury/submission/${n._id}/jury-status`, { juryStatus: target, sendEmail: false });
      toast.success(`Moved back to ${label}`);
      fetchNominations();
    } catch { toast.error("Could not revert this nomination"); }
  };

  const openRemarks = (submissionId, status, label) => {
    setRemarksModal({ submissionId, status, label });
    setRemarksText(""); setSendEmail(true);
  };

  const confirmStatus = async () => {
    if (!remarksModal) return;
    try {
      await api.patch(`/jury/submission/${remarksModal.submissionId}/jury-status`, {
        juryStatus: remarksModal.status,
        finalRemarks: remarksText || undefined,
        sendEmail,
      });
      toast.success(`Marked as ${remarksModal.label}`);
      setRemarksModal(null); fetchNominations();
    } catch { toast.error("Failed"); }
  };

  // ── Bulk actions ────────────────────────────────────────────────────────────
  const allSelected = displayed.length > 0 && displayed.every(n => selectedIds.includes(n._id));

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : displayed.map(n => n._id));
  };

  const toggleSelect = (id) => {
    setSelectedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  };

  // Applies one status to every selected nomination, reporting what actually landed
  const runBulk = async (status, label, sendEmailForBulk) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkRunning(true);
    let ok = 0, failed = 0;
    for (const id of ids) {
      try {
        await api.patch(`/jury/submission/${id}/jury-status`, {
          juryStatus: status,
          sendEmail: !!sendEmailForBulk,
        });
        ok++;
      } catch { failed++; }
    }
    setBulkRunning(false);
    setBulkConfirm(null);
    setSelectedIds([]);
    fetchNominations();
    if (failed === 0) toast.success(`${ok} nomination${ok === 1 ? "" : "s"} marked ${label}`);
    else toast.warning(`${ok} marked ${label}, ${failed} failed`);
  };

  const canTakeAction = (status) =>
    ["scored","sent_to_jury","winner","not_selected","honorary"].includes(status);

  const exportExcel = () => {
    const rows = displayed.map(n => {
      const sd  = n.submissionData || {};
      const row = {
        Name:            getNomineeName(sd),
        Email:           getNomineeEmail(sd),
        Category:        n.nominationCategory || "",
        "Sub-category":  n.nominationSubCategory || "",
        "Jury Status":   STATUS_LABELS[n.juryStatus] || n.juryStatus,
        "Avg Score":     n.averageScore != null ? n.averageScore : "",
        "Payment Status":n.paymentStatus || "",
        "Submitted At":  new Date(n.submittedAt || n.createdAt).toLocaleDateString("en-IN"),
      };
      Object.entries(sd).forEach(([k, v]) => {
        const norm = k.toLowerCase().replace(/[\s_]/g, "");
        if (!k.startsWith("_") && !["leadsource","leadtype","crmeventid","leadownerid","eventid","formid","zohoStatus","zohoLeadId","salutation"].includes(norm)) {
          row[k] = String(v);
        }
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Nominations");
    XLSX.writeFile(wb, `nominations-${tab}-${eventId}.xlsx`);
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const SortIcon = ({ col }) => sortBy !== col ? (
    <span style={{ color: "#ccc", marginLeft: 4 }}>↕</span>
  ) : (
    <span style={{ color: "#1D9AEC", marginLeft: 4 }}>{sortDir === "asc" ? "↑" : "↓"}</span>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Layout title="Nominations">

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, width: 400, boxShadow: "0 8px 32px rgba(0,0,0,0.15)", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🗑</div>
            <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 700, color: "#0F172A" }}>Delete Nomination?</h3>
            <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 20px", lineHeight: 1.6 }}>
              Permanently delete <strong style={{ color: "#0F172A" }}>{deleteConfirm.name}</strong>?<br/>This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ ...btnSmall("#94A3B8"), padding: "8px 20px" }}>Cancel</button>
              <button onClick={() => deleteNomination(deleteConfirm.id)} style={{ ...btnSmall("#dc2626"), padding: "8px 20px" }}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Remarks modal */}
      {remarksModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, width: 420, boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
            <h3 style={{ margin: "0 0 6px", fontSize: 16 }}>Mark as {remarksModal.label}</h3>
            <p style={{ fontSize: 13, color: "#888", margin: "0 0 16px" }}>Add final remarks (optional)</p>
            <textarea value={remarksText} onChange={e => setRemarksText(e.target.value)} rows={3}
              placeholder="Enter remarks…"
              style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: 8, fontSize: 14, resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, cursor: "pointer", fontSize: 13, color: "#374151" }}>
              <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)}
                style={{ accentColor: STATUS_COLOR[remarksModal.status], width: 15, height: 15 }} />
              Send email notification to nominee
            </label>
            <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
              <button onClick={() => setRemarksModal(null)} style={btnSmall("#9ca3af")}>Cancel</button>
              <button onClick={confirmStatus} style={btnSmall(STATUS_COLOR[remarksModal.status])}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editTarget && (
        <EditModal
          nomination={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={fetchNominations}
        />
      )}

      {/* Add nominee modal */}
      {addModal && (
        <AddNomineeModal
          eventId={eventId}
          nominationType={tab}
          onClose={() => setAddModal(false)}
          onSaved={fetchNominations}
        />
      )}

      {/* Import nominations modal */}
      {importModal && (
        <ImportNominationsModal
          eventId={eventId}
          nominationType={tab}
          onClose={() => setImportModal(false)}
          onSaved={fetchNominations}
        />
      )}

      {/* Back to events */}
      <button onClick={() => navigate("/admin/events")} style={{ marginBottom: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#475569", display: "inline-flex", alignItems: "center", gap: 6, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        ← Back to Events
      </button>

      {/* Top bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        {/* Tabs */}
        <div style={{ display: "flex", gap: 8 }}>
          {["individual", "organisation"].map(t => (
            <button key={t} onClick={() => handleTabSwitch(t)} style={{
              padding: "7px 20px", borderRadius: 20, border: "none", cursor: "pointer",
              fontWeight: 600, fontSize: 13,
              background: tab === t ? "#1D9AEC" : "#e8e8e8",
              color: tab === t ? "#fff" : "#555",
            }}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
              <span style={{ marginLeft: 6, background: tab === t ? "rgba(255,255,255,0.25)" : "#d1d5db", color: tab === t ? "#fff" : "#6b7280", borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>
                {tabCounts[t] ?? 0}
              </span>
            </button>
          ))}
          {/* View mode toggle */}
          <div style={{ display: "flex", gap: 6, marginLeft: 6 }}>
            <button onClick={() => setViewMode("list")} style={{
              padding: "6px 14px", borderRadius: 8, cursor: "pointer",
              fontWeight: 600, fontSize: 12, transition: "all 0.15s",
              border: viewMode === "list" ? "1.5px solid #1D9AEC" : "1.5px solid #E2E8F0",
              background: viewMode === "list" ? "#EFF8FF" : "#fff",
              color: viewMode === "list" ? "#1D9AEC" : "#94A3B8",
            }}>☰ List</button>
            <button onClick={() => setViewMode("categories")} style={{
              padding: "6px 14px", borderRadius: 8, cursor: "pointer",
              fontWeight: 700, fontSize: 12, transition: "all 0.15s",
              border: viewMode === "categories" ? "1.5px solid #6366F1" : "1.5px solid #E2E8F0",
              background: viewMode === "categories" ? "#EEF2FF" : "#fff",
              color: viewMode === "categories" ? "#6366F1" : "#94A3B8",
              boxShadow: viewMode === "categories" ? "0 0 0 3px rgba(99,102,241,0.1)" : "none",
            }}>⊞ By Category</button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={exportExcel}
            style={{ background: "#fefce8", color: "#a16207", border: "1px solid #fde68a", padding: "7px 16px", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            ⬆ Export Excel
          </button>
          <button onClick={() => setImportModal(true)}
            style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", padding: "7px 16px", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            ⬇ Import Excel
          </button>
          <button onClick={() => setAddModal(true)}
            style={{ background: "#eff6ff", color: "#1D9AEC", border: "1px solid #bfdbfe", padding: "7px 16px", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            + Add Nominee
          </button>
          <button onClick={() => navigate(`/admin/events/${eventId}/coverage`)}
            style={{ background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd", padding: "7px 16px", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
            ◷ Panel Coverage
          </button>
          <button onClick={() => navigate(`/admin/events/${eventId}/email-templates`)}
            style={{ background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd", padding: "7px 16px", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
            ✉ Email Templates
          </button>
        </div>
      </div>

      {/* Bulk action bar — appears once rows are selected */}
      {selectedIds.length > 0 && (
        <div style={{
          position: "sticky", top: 68, zIndex: 60, marginBottom: 14,
          background: "#0F172A", borderRadius: 10, padding: "12px 16px",
          display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
          boxShadow: "0 6px 20px rgba(15,23,42,0.25)",
        }}>
          <span style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>
            {selectedIds.length} selected
          </span>
          <button onClick={() => setSelectedIds([])}
            style={{ background: "none", border: "none", color: "#94A3B8", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            Clear
          </button>
          <span style={{ height: 18, width: 1, background: "#334155" }} />
          <button onClick={() => setBulkConfirm({ status: "sent_to_jury", label: "Sent to Jury" })}
            style={bulkBtn("#f59e0b")}>Send to Jury</button>
          <button onClick={() => setBulkConfirm({ status: "winner", label: "Winner", email: true })}
            style={bulkBtn("#10b981")}>Winner</button>
          <button onClick={() => setBulkConfirm({ status: "honorary", label: "Honorary", email: true })}
            style={bulkBtn("#0891b2")}>Honorary</button>
          <button onClick={() => setBulkConfirm({ status: "not_selected", label: "Rejected", email: true })}
            style={bulkBtn("#ef4444")}>Rejected</button>
        </div>
      )}

      {/* Bulk confirmation — spells out the email consequence before it fires */}
      {bulkConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 26, width: "100%", maxWidth: 420, boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 17, fontWeight: 700, color: "#0F172A" }}>
              Mark {selectedIds.length} nomination{selectedIds.length === 1 ? "" : "s"} as {bulkConfirm.label}?
            </h3>
            <p style={{ margin: "0 0 16px", fontSize: 13.5, color: "#64748B", lineHeight: 1.6 }}>
              {bulkConfirm.email
                ? "This is the status nominees are told about. Choose below whether they hear about it now."
                : "This opens the nominations to every juror assigned to the event."}
            </p>

            {bulkConfirm.email && (
              <label style={{
                display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer",
                padding: "12px 14px", borderRadius: 9, marginBottom: 16,
                background: bulkEmail ? "#FFFBEB" : "#F8FAFC",
                border: `1.5px solid ${bulkEmail ? "#FDE68A" : "#E2E8F0"}`,
              }}>
                <input type="checkbox" checked={bulkEmail} onChange={e => setBulkEmail(e.target.checked)}
                  style={{ width: 16, height: 16, marginTop: 1, accentColor: "#F59E0B", cursor: "pointer" }} />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: bulkEmail ? "#92400E" : "#475569", lineHeight: 1.5 }}>
                  {bulkEmail
                    ? `Send the ${bulkConfirm.label.toLowerCase()} email to all ${selectedIds.length} nominees now`
                    : "No email will be sent — you can notify them later"}
                </span>
              </label>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setBulkConfirm(null)} disabled={bulkRunning}
                style={{ background: "#fff", color: "#475569", border: "1px solid #E2E8F0", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button
                onClick={() => runBulk(bulkConfirm.status, bulkConfirm.label, bulkConfirm.email && bulkEmail)}
                disabled={bulkRunning}
                style={{ background: bulkRunning ? "#CBD5E1" : "#1D9AEC", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: bulkRunning ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {bulkRunning ? "Applying…" : `Mark ${bulkConfirm.label}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active category breadcrumb */}
      {catFilter && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, padding: "10px 16px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10 }}>
          <span style={{ fontSize: 13, color: "#64748b" }}>
            <span style={{ color: "#94a3b8", cursor: "pointer", fontWeight: 600 }} onClick={() => { setCatFilter(""); setSubFilter(""); setViewMode("categories"); }}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </span>
            {" › "}
            <strong style={{ color: "#1D9AEC" }}>{catFilter}</strong>
          </span>
          <span style={{ background: "#1D9AEC", color: "#fff", borderRadius: 10, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>{displayed.length}</span>
          <button onClick={() => { setCatFilter(""); setSubFilter(""); setViewMode("categories"); }}
            style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 13, fontWeight: 600, padding: "2px 8px" }}>
            ← All Categories
          </button>
        </div>
      )}

      {/* Sub-category filter — only for categories that have a second level */}
      {catFilter && subBreakdown.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em", marginRight: 2 }}>
            Sub-category
          </span>
          {[{ name: "", count: subBreakdown.reduce((t, s) => t + s.count, 0) }, ...subBreakdown].map(s => {
            const active = subFilter === s.name;
            const label  = s.name === "" ? "All" : s.name;
            return (
              <button key={s.name || "__all"} onClick={() => setSubFilter(s.name)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
                  background: active ? "#6d28d9" : "#fff", color: active ? "#fff" : "#4b5563",
                  border: `1px solid ${active ? "#6d28d9" : "#e5e7eb"}`,
                  padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                }}>
                {label}
                <span style={{ fontSize: 10.5, fontWeight: 700, background: active ? "rgba(255,255,255,0.25)" : "#f3f4f6", color: active ? "#fff" : "#6b7280", padding: "1px 7px", borderRadius: 10 }}>
                  {s.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Category grid view — Individual + Organisation as separate sections ── */}
      {viewMode === "categories" && !catFilter && (
        !nominations ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px,1fr))", gap: 12 }}>
            {[1,2,3,4,5,6].map(i => (
              <div key={i} style={{ background: "#fff", borderRadius: 10, padding: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                <div className="skeleton" style={{ height: 12, width: "70%", marginBottom: 8 }} />
                <div className="skeleton" style={{ height: 8, width: "40%", marginBottom: 10 }} />
                <div className="skeleton" style={{ height: 4, width: "100%" }} />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>

            {/* Global category search */}
            <div style={{ position: "relative", maxWidth: 360 }}>
              <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94A3B8" }}>🔍</span>
              <input
                type="text"
                placeholder="Search categories…"
                value={catSearch}
                onChange={e => setCatSearch(e.target.value)}
                style={{ width: "100%", padding: "8px 14px 8px 34px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, outline: "none", background: "#fff", boxSizing: "border-box", fontFamily: "inherit", color: "#0F172A" }}
                onFocus={e => { e.target.style.borderColor = "#6366F1"; }}
                onBlur={e => { e.target.style.borderColor = "#E2E8F0"; }}
              />
              {catSearch && (
                <button onClick={() => setCatSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 14, lineHeight: 1 }}>✕</button>
              )}
            </div>

            {[
              { type: "individual",   label: "Individual",   icon: "👤", accent: "#1D9AEC", lightBg: "#EFF8FF" },
              { type: "organisation", label: "Organisation", icon: "🏢", accent: "#6366F1", lightBg: "#EEF2FF" },
            ].map(({ type, label, icon, accent, lightBg }) => {
              const q    = catSearch.trim().toLowerCase();
              const all  = catsByType[type];
              const cats = q ? all.filter(c => c.name.toLowerCase().includes(q)) : all;
              const totalNoms = all.reduce((s, c) => s + c.total, 0);

              return (
                <div key={type}>
                  {/* Section header */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, background: lightBg, border: `1px solid ${accent}25`, borderRadius: 10, padding: "6px 14px" }}>
                      <span style={{ fontSize: 15 }}>{icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: accent }}>{label}</span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: "#64748B", background: "#F8FAFC", border: "1px solid #E2E8F0", padding: "3px 10px", borderRadius: 20 }}>
                        {all.length} categories
                      </span>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: "#64748B", background: "#F8FAFC", border: "1px solid #E2E8F0", padding: "3px 10px", borderRadius: 20 }}>
                        {totalNoms} nominations
                      </span>
                    </div>
                    <span style={{ height: 1, flex: 1, background: "#E2E8F0" }} />
                    {q && <span style={{ fontSize: 11.5, color: "#94A3B8" }}>{cats.length} match{cats.length !== 1 ? "es" : ""}</span>}
                  </div>

                  {/* Cards */}
                  {cats.length === 0 ? (
                    <div style={{ background: "#fff", borderRadius: 10, padding: "20px 24px", textAlign: "center", border: "1px dashed #E2E8F0" }}>
                      <p style={{ fontWeight: 500, color: "#94A3B8", margin: 0, fontSize: 13 }}>
                        {q ? `No categories matching "${catSearch}"` : `No ${label.toLowerCase()} categories yet`}
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(185px, 1fr))", gap: 10 }}>
                      {cats.map((cat, idx) => {
                        const color     = CAT_PALETTE[idx % CAT_PALETTE.length];
                        const scoredPct = cat.total > 0 ? Math.round(((cat.scored + cat.decided) / cat.total) * 100) : 0;
                        const pct       = totalNoms > 0 ? Math.round((cat.total / totalNoms) * 100) : 0;
                        return (
                          <div key={cat.name}
                            onClick={() => { setTab(type); setCatFilter(cat.name); setSubFilter(""); setViewMode("list"); setCatSearch(""); }}
                            style={{ background: "#fff", borderRadius: 10, padding: "14px 14px 12px", border: `1px solid ${color}28`, cursor: "pointer", position: "relative", overflow: "hidden", transition: "transform 0.15s, box-shadow 0.15s", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}
                            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 6px 20px ${color}25`; e.currentTarget.style.borderColor = color; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)"; e.currentTarget.style.borderColor = `${color}28`; }}
                          >
                            {/* Top accent bar */}
                            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2.5, background: color }} />

                            {/* Count + share */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                              <span style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{cat.total}</span>
                              <span style={{ fontSize: 10, fontWeight: 700, color: "#94A3B8", background: "#F8FAFC", padding: "2px 6px", borderRadius: 6 }}>{pct}%</span>
                            </div>

                            {/* Name */}
                            <p style={{ margin: "0 0 8px", fontSize: 12.5, fontWeight: 700, color: "#0F172A", lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{cat.name}</p>

                            {/* Status pills — compact */}
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                              {cat.not_sent > 0 && <span style={{ fontSize: 9.5, fontWeight: 700, background: "#F1F5F9", color: "#94A3B8", padding: "1px 6px", borderRadius: 6 }}>{cat.not_sent} draft</span>}
                              {cat.sent    > 0 && <span style={{ fontSize: 9.5, fontWeight: 700, background: "#FFFBEB", color: "#D97706", padding: "1px 6px", borderRadius: 6 }}>{cat.sent} jury</span>}
                              {cat.scored  > 0 && <span style={{ fontSize: 9.5, fontWeight: 700, background: "#ECFDF5", color: "#059669", padding: "1px 6px", borderRadius: 6 }}>{cat.scored} scored</span>}
                              {cat.decided > 0 && <span style={{ fontSize: 9.5, fontWeight: 700, background: "#EEF2FF", color: "#4F46E5", padding: "1px 6px", borderRadius: 6 }}>{cat.decided} done</span>}
                            </div>

                            {/* Progress bar */}
                            <div style={{ background: "#F1F5F9", borderRadius: 3, height: 4, overflow: "hidden", marginBottom: cat.avgScore != null ? 6 : 0 }}>
                              <div style={{ width: `${scoredPct}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.4s" }} />
                            </div>

                            {cat.avgScore != null && (
                              <p style={{ margin: "5px 0 0", fontSize: 10.5, color: "#64748B" }}>
                                Avg: <strong style={{ color }}>{cat.avgScore}/10</strong>
                              </p>
                            )}

                            {/* Sub-category split */}
                            {cat.subList.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8, paddingTop: 8, borderTop: "1px dashed #E2E8F0" }}>
                                {cat.subList.slice(0, 3).map(s => (
                                  <span key={s.name} title={`${s.name} — ${s.count}`}
                                    style={{ fontSize: 9.5, fontWeight: 700, background: "#F5F3FF", color: "#6D28D9", padding: "1px 6px", borderRadius: 6, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {s.name} · {s.count}
                                  </span>
                                ))}
                                {cat.subList.length > 3 && (
                                  <span style={{ fontSize: 9.5, fontWeight: 700, color: "#94A3B8" }}>+{cat.subList.length - 3}</span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── List view ────────────────────────────────────────────────────── */}
      {(viewMode === "list" || catFilter) && (<>
        {/* Toolbar: search + filter + sort — hidden in pure category view */}
        <div style={{ background: "#fff", borderRadius: 10, padding: "14px 16px", marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
            <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#9ca3af" }}>🔍</span>
            <input type="text" placeholder="Search by name, email or category…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", padding: "9px 14px 9px 34px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box", outline: "none", background: "#f9fafb" }} />
            {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 15 }}>✕</button>}
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, outline: "none", background: "#f9fafb", cursor: "pointer" }}>
            <option value="all">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={payFilter} onChange={e => setPayFilter(e.target.value)} style={{ padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, outline: "none", background: "#f9fafb", cursor: "pointer" }}>
            <option value="all">All Payments</option>
            {Object.entries(PAY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, outline: "none", background: "#f9fafb", cursor: "pointer" }}>
            <option value="name">Sort: Name</option>
            <option value="score">Sort: Score</option>
            <option value="status">Sort: Status</option>
          </select>
          <button onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")} style={{ padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, background: "#f9fafb", cursor: "pointer", fontWeight: 700, color: "#374151" }}>
            {sortDir === "asc" ? "↑ Asc" : "↓ Desc"}
          </button>
        </div>

        {/* Result count */}
        <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 10 }}>
          Showing <strong style={{ color: "#374151" }}>{displayed.length}</strong>
          {nominations && <> of <strong style={{ color: "#374151" }}>{nominations.filter(n => n.eventFormId?.nominationType === tab).length}</strong></>} nominations
          {catFilter && <> in <strong style={{ color: "#1D9AEC" }}>{catFilter}</strong></>}
          {search && <> matching "<strong style={{ color: "#1D9AEC" }}>{search}</strong>"</>}
        </p>

        {/* Table */}
        {!nominations ? (
          <p style={{ color: "#888" }}>Loading…</p>
        ) : displayed.length === 0 ? (
          <div style={{ background: "#fff", borderRadius: 10, padding: 48, textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <p style={{ fontSize: 32, margin: "0 0 10px" }}>🔍</p>
            <p style={{ fontWeight: 600, color: "#374151", margin: "0 0 4px" }}>No nominations found</p>
            <p style={{ color: "#9ca3af", fontSize: 13 }}>Try changing your search or filter</p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
              <thead>
                <tr style={{ background: "#f8f9fa" }}>
                  <th style={{ ...th, width: 38 }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                      aria-label="Select all shown nominations"
                      style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#1D9AEC" }} />
                  </th>
                  <th style={{ ...th, cursor: "pointer" }} onClick={() => toggleSort("name")}>Nominee <SortIcon col="name" /></th>
                  <th style={th}>Category</th>
                  <th style={{ ...th, cursor: "pointer" }} onClick={() => toggleSort("status")}>Status <SortIcon col="status" /></th>
                  <th style={{ ...th, cursor: "pointer" }} onClick={() => toggleSort("score")}>Avg Score <SortIcon col="score" /></th>
                  <th style={th}>Research</th>
                  <th style={th}>Payment</th>
                  <th style={th}>Scores</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((n, idx) => (
                  <tr key={n._id} style={{ borderTop: idx > 0 ? "1px solid #f0f0f0" : "none", background: selectedIds.includes(n._id) ? "#F0F9FF" : "transparent" }}>
                    <td style={td}>
                      <input type="checkbox" checked={selectedIds.includes(n._id)} onChange={() => toggleSelect(n._id)}
                        aria-label={`Select ${getNomineeName(n.submissionData)}`}
                        style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#1D9AEC" }} />
                    </td>
                    <td style={td}>
                      <div>
                        <span style={{ cursor: "pointer", color: "#1D9AEC", fontWeight: 600, fontSize: 14 }} onClick={() => navigate(`/admin/submissions/${n._id}`)}>
                          {getNomineeName(n.submissionData)}
                        </span>
                        {getNomineeEmail(n.submissionData) && (
                          <p style={{ margin: "2px 0 0", fontSize: 11, color: "#9ca3af" }}>{getNomineeEmail(n.submissionData)}</p>
                        )}
                      </div>
                    </td>
                    <td style={{ ...td, maxWidth: 180 }}>
                      <span onClick={() => { setCatFilter(n.nominationCategory || "Uncategorized"); setSubFilter(""); setViewMode("list"); }}
                        style={{ cursor: "pointer" }} title="Filter by this category">
                        <CategoryBadge value={n.nominationCategory} sub={n.nominationSubCategory} />
                      </span>
                    </td>
                    <td style={td}><StatusBadge status={n.juryStatus} /></td>
                    <td style={td}>
                      {n.averageScore != null ? (
                        <>
                          <span style={{ fontWeight: 700, color: "#1D9AEC", fontSize: 16 }}>{n.averageScore}/10</span>
                          <QuorumNote scored={n.scoreCount} expected={n.expectedScorers ?? panelSize} />
                        </>
                      ) : (
                        <>
                          <span style={{ color: "#cbd5e1", fontSize: 16 }}>—</span>
                          <QuorumNote scored={n.scoreCount} expected={n.expectedScorers ?? panelSize} />
                        </>
                      )}
                    </td>
                    <td style={td}>
                      {n.researchNotes?.length > 0 ? (
                        <span title={`${n.researchNotes.length} research entries`}
                          style={{ fontSize: 11.5, fontWeight: 700, background: "#EEF2FF", color: "#4F46E5", padding: "3px 9px", borderRadius: 20 }}>
                          {n.researchNotes.length} added
                        </span>
                      ) : (
                        <span style={{ fontSize: 11.5, color: "#cbd5e1" }}>none</span>
                      )}
                    </td>
                    <td style={td}><PayBadge status={n.paymentStatus} /></td>
                    <td style={td}>
                      <button onClick={() => navigate(`/admin/submissions/${n._id}`)} style={btnSmall("#6366f1")}>View Scores</button>
                    </td>
                    <td style={{ ...td, minWidth: 260 }}>
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                        <button onClick={() => setEditTarget(n)} style={btnSmall("#374151")} title="Edit details">✏ Edit</button>
                        {n.juryStatus === "not_sent" && (
                          <button onClick={() => sendToJury(n._id)} style={btnSmall("#f59e0b")}>Send to Jury</button>
                        )}
                        {n.juryStatus !== "not_sent" && (
                          <button onClick={() => revertStatus(n)} style={btnSmall("#64748b")}
                            title="Move this nomination back a step">↩ Revert</button>
                        )}
                        {canTakeAction(n.juryStatus) && (
                          <>
                            <button onClick={() => openRemarks(n._id, "winner",       "Winner")}   style={btnSmall("#10b981")}>Winner</button>
                            <button onClick={() => openRemarks(n._id, "honorary",     "Honorary")} style={btnSmall("#0891b2")}>Honorary</button>
                            <button onClick={() => openRemarks(n._id, "not_selected", "Rejected")} style={btnSmall("#ef4444")}>Rejected</button>
                          </>
                        )}
                        <button onClick={() => setDeleteConfirm({ id: n._id, name: getNomineeName(n.submissionData) })} style={{ ...btnSmall("#dc2626"), marginLeft: 2 }} title="Delete nomination">🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>)}
    </Layout>
  );
}

const th       = { padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.04em", userSelect: "none" };
const td       = { padding: "12px 16px", fontSize: 14, color: "#333", verticalAlign: "middle" };
const btnSmall = (bg) => ({ background: bg, color: "#fff", border: "none", padding: "5px 11px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", fontFamily: "inherit" });
const bulkBtn  = (bg) => ({ background: bg, color: "#fff", border: "none", padding: "7px 14px", borderRadius: 7, cursor: "pointer", fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap", fontFamily: "inherit" });
const lbl      = { display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 5 };
const inp      = { width: "100%", padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box", outline: "none", fontFamily: "inherit" };
